import React from 'react';
import {NumericFormat} from 'react-number-format';
import {FindLoad, formatNumberToFix, renderDateRange, saveLocalParams} from 'utils';
import {Skeleton} from '@mui/material';
import {useSelector} from 'react-redux';
import {SelectedFilters} from 'components';
import {claimPaymentStatus, remittanceSourceList} from '../constants';

const ACTION_TYPE = 'GET_REMITTANCE_TOTAL';

const persistClaimFilters = (info) => {
    const localParams = {};
    if (info?.payer) localParams.payer = info.payer;
    // `funderName` is the display-name companion for `payer` across both billing pages and is
    // what the payer dropdown and the chip read back — keep the snapshot on the same key.
    if (info?.funderName) localParams.funderName = info.funderName;
    if (info?.createdFrom) localParams.createdFrom = info.createdFrom;
    if (info?.createdTo) localParams.createdTo = info.createdTo;
    if (info?.paymentDateFrom) localParams.paymentDateFrom = info.paymentDateFrom;
    if (info?.paymentDateTo) localParams.paymentDateTo = info.paymentDateTo;
    saveLocalParams('claimFilters', localParams);
};

export const ClaimPaymentTotals = ({}) => {
    const loader = FindLoad(ACTION_TYPE);
    const remittanceTotal = useSelector((state) => state.claimPayment.remittanceTotal);
    const filtersList = [
        {name: 'Payer', key: 'payer', companions: ['funderName'], render: (v, state) => state?.funderName || v},
        {name: 'Source', key: 'source', options: remittanceSourceList},
        {name: 'Created', keys: ['createdFrom', 'createdTo'], render: ([from, to]) => renderDateRange(from, to)},
        {name: 'Payment', keys: ['paymentDateFrom', 'paymentDateTo'], render: ([from, to]) => renderDateRange(from, to)},
        {label: 'Verified only', key: 'verified'},
        {label: 'Needs review', key: 'needsReview'},
        // The remittances table's "Status" column filter — a real filter on `location.state` like
        // any other, so it belongs in the same bar. Three distinct states here, and only one is a
        // chip: absent is the column's own Open + Applied default (`defaultSelected`, re-applied by
        // `renderParams`), `[]` is the "All" option (no status filter at all), and a non-empty array
        // is the only one the user actually chose. Removing the last chip deletes the key, which is
        // what resets the grid to that default.
        {name: 'Status', key: 'statuses', options: claimPaymentStatus},
    ];

    /**
     * Keep the shared `claimFilters` sessionStorage snapshot in step with the state write.
     *
     * That key is read back by the Claims page on mount, so a filter removed here has to leave it
     * too — otherwise it comes back the next time the user opens `/claims`.
     */
    const handleFilterChange = (params) => persistClaimFilters(params);

    if (loader?.length) {
        return (
            <div style={{marginBottom: '-6px'}}>
                <Skeleton variant="rectangular" width={'100%'} height={66} style={{borderRadius: '8px 8px 0px 0px'}}/>
            </div>
        );
    }

    return (
        <div>
            <SelectedFilters
                filtersList={filtersList}
                resetParams={{skip: 0, limit: 50, page: 1}}
                onChange={handleFilterChange}
                doNotCrop
            />

            <div className="billing-total-box space-between" style={{gap: 16}}>
                <div/>
                <div className="flex-align-center" style={{gap: 16}}>
                    <div className="billing-total-row">
                        <p className="billing-total-title">Total Amount:</p>
                        <div className="billing-total-price-row">
                            <NumericFormat
                                // value={remittanceTotal?.totalAmount ? remittanceTotal?.totalAmount.toFixed(3) : 0}
                                value={remittanceTotal?.totalAmount ? formatNumberToFix(remittanceTotal?.totalAmount) : 0}
                                displayType={'text'}
                                thousandSeparator={true}
                                prefix={'$'}
                            />
                        </div>
                    </div>
                    <div className="billing-total-row">
                        <p className="billing-total-title">Total Applied:</p>
                        <div className="billing-total-price-row">
                            <NumericFormat
                                // value={remittanceTotal?.appliedAmount ? remittanceTotal?.appliedAmount.toFixed(3) : 0}
                                value={remittanceTotal?.appliedAmount ? formatNumberToFix(remittanceTotal?.appliedAmount) : 0}
                                displayType={'text'}
                                thousandSeparator={true}
                                prefix={'$'}
                            />
                        </div>
                    </div>
                    <div className="billing-total-row">
                        <p className="billing-total-title">Total Open:</p>
                        <div className="billing-total-price-row">
                            <NumericFormat
                                // value={remittanceTotal?.openAmount ? remittanceTotal?.openAmount.toFixed(3) : 0}
                                value={remittanceTotal?.openAmount ? formatNumberToFix(remittanceTotal?.openAmount) : 0}
                                displayType={'text'}
                                thousandSeparator={true}
                                prefix={'$'}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
